import { FocusEvent, KeyboardEvent, useRef, useState } from 'react';
import { Dropdown } from 'antd';
import FeatherIcon from 'feather-icons-react';
import { ISearchPicker } from './types';
import { TeamPickerField, TeamPickerInput, TeamPickerPanel } from './styles';
import { colors } from 'constants/styles/colors';

// A search field that opens a list of tick boxes in a dialog.
const SearchPicker: React.FC<ISearchPicker> = ({
  panelId,
  label,
  searchLabel,
  placeholder,
  query,
  onQueryChange,
  input: SearchInput = TeamPickerInput,
  chevronSize = 'default',
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [panelWidth, setPanelWidth] = useState<number>();
  const rootRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const setVisible = (visible: boolean) => {
    // Match the panel to the field's width.
    if (visible) setPanelWidth(fieldRef.current?.offsetWidth);
    else onQueryChange('');
    setOpen(visible);
  };

  // The enabled tick boxes, in list order.
  const tickBoxes = () =>
    Array.from(
      panelRef.current?.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"]:not(:disabled)'
      ) ?? []
    );

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      // The first press opens the list; the next one moves to its first control.
      if (!open) setVisible(true);
      else
        panelRef.current
          ?.querySelector<HTMLElement>('input:not(:disabled), button:not(:disabled)')
          ?.focus();
    } else if (event.key === 'Escape' && open) {
      // Closes the list, not the invite modal around it.
      event.stopPropagation();
      setVisible(false);
    }
  };

  // Close the list only when focus leaves both the field and the list.
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (open && !rootRef.current?.contains(event.relatedTarget as Node | null)) setVisible(false);
  };

  const handlePanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setVisible(false);
      inputRef.current?.focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    // The arrows step between the tick boxes; up from the first goes back to the search.
    const boxes = tickBoxes();
    const index = boxes.indexOf(event.target as HTMLInputElement);
    if (index === -1) return;
    event.preventDefault();
    const next = index + (event.key === 'ArrowDown' ? 1 : -1);
    if (next < 0) inputRef.current?.focus();
    else boxes[Math.min(next, boxes.length - 1)].focus();
  };

  const panel = (
    <div
      ref={panelRef}
      id={panelId}
      role="dialog"
      aria-label={label}
      onKeyDown={handlePanelKeyDown}
      // Keep focus in the search when clicking the list.
      onMouseDown={(event) => event.preventDefault()}
    >
      <TeamPickerPanel style={{ width: panelWidth }}>{children}</TeamPickerPanel>
    </div>
  );

  return (
    <div ref={rootRef} onBlur={handleBlur}>
      <Dropdown
        overlay={panel}
        visible={open}
        onVisibleChange={setVisible}
        trigger={['click']}
        placement="bottomLeft"
        destroyPopupOnHide
        // Above the modal; mounted next to the field so it scrolls and tabs with it.
        overlayStyle={{ zIndex: 9999 }}
        getPopupContainer={() => rootRef.current ?? document.body}
      >
        <TeamPickerField ref={fieldRef} $open={open} onClick={() => inputRef.current?.focus()}>
          <FeatherIcon
            icon="search"
            size={16}
            color={colors.palette.Business_LightThemeGreyDarker}
          />
          <SearchInput
            ref={inputRef}
            value={query}
            placeholder={placeholder}
            role="combobox"
            aria-label={searchLabel}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={panelId}
            aria-autocomplete="list"
            onChange={(event) => {
              onQueryChange(event.target.value);
              if (!open) setVisible(true);
            }}
            // A click in the text only opens the list.
            onClick={(event) => {
              event.stopPropagation();
              if (!open) setVisible(true);
            }}
            onKeyDown={handleKeyDown}
          />
          <FeatherIcon icon="chevron-down" size={chevronSize === 'small' ? 16 : 24} />
        </TeamPickerField>
      </Dropdown>
    </div>
  );
};

export default SearchPicker;
